import { useState } from 'react';
import { v4 as uuid } from 'uuid';
import { DeleteOutline } from '@mui/icons-material';

import { Button, Copy, Input } from '..';
import { useIsMounted } from '../../hooks';
import type { InboundPhoneNumberType as PhoneType } from '../../api';

type PhonesInputProps = {
  values: PhoneType[];
  loading?: boolean;
  disabled?: boolean;

  onDelete?: (phone: PhoneType) => Promise<void>;
  onAdd?: (phoneNumber: string) => Promise<string | undefined>;
};

export const PhonesInput: React.FC<PhonesInputProps> = ({
  loading,
  values,
  disabled: defaultDisabled,

  onAdd,
  onDelete,
}) => {
  const isMounted = useIsMounted();
  const disabled = defaultDisabled || loading;
  const [localValues, setValues] = useState(values || []);

  return (
    <>
      <div className="flex flex-col gap-y-md mb-md">
        {localValues.map((phone, index) => {
          const exists = values?.some(v => v.id === phone.id);

          return (
            <div key={phone.id} className="flex flex-row gap-x-lg items-center">
              <Input
                key={index}
                disabled={disabled}
                value={phone.phoneNumber}
                onChange={phoneNumber => {
                  setValues(old =>
                    old.map(o =>
                      o.id === phone.id ? { ...phone, phoneNumber } : o
                    )
                  );
                }}
              />
              {exists ? (
                <div className="flex flex-row gap-x-md items-center">
                  <Copy
                    label="Copy ID"
                    text={phone.id || ''}
                    className="text-buttonPrimary border border-buttonPrimary rounded-xl px-xs py-xs gap-x-xs hover:bg-select"
                    iconClassName="text-small"
                    labelClassName="text-small"
                  />
                  <Button
                    type="primary"
                    loading={loading}
                    disabled={disabled}
                    icon={<DeleteOutline className="text-small" />}
                    onClick={async () => {
                      await onDelete?.(phone);

                      if (!isMounted.current) return;
                      setValues(old => old.filter((_v, i) => i !== index));
                    }}
                  />
                </div>
              ) : (
                <Button
                  type="light"
                  label="Save"
                  onClick={async () => {
                    const newId = await onAdd?.(phone?.phoneNumber);
                    if (!newId || !isMounted.current) return;

                    setValues(old =>
                      old.map(o =>
                        o.id === phone.id ? { ...o, id: newId } : o
                      )
                    );
                  }}
                  disabled={disabled || !phone?.phoneNumber?.length}
                />
              )}
            </div>
          );
        })}
      </div>
      <Button
        label="Add new"
        disabled={disabled}
        onClick={() =>
          setValues(old => [...old, { id: uuid(), phoneNumber: '' }])
        }
      />
    </>
  );
};
